import React from 'react'
import {
  Users as UsersIcon,
  ShieldCheck,
  Search,
  UserCog,
  MoreVertical,
  Check,
  X,
  ArrowRight,
  Info,
  Zap,
  AlertCircle,
  FileStack,
  Settings,
} from 'lucide-react'
import { Table } from './GuideParts'

// ---------------------------------------------------------------------------
// Mockup: Stats Cards Row
// ---------------------------------------------------------------------------
function StatsCardsMockup() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <UsersIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">8</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Active Users</p>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">2</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Administrators</p>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <UsersIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">1</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">Inactive</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Users Table
// ---------------------------------------------------------------------------
function UsersTableMockup() {
  const users = [
    { name: 'Sarah Johnson', email: 'sarah.johnson@company.com', jobTitle: 'Operations Manager', role: 'admin', status: 'active', lastLogin: '22 May 2026', isSelf: true, initials: 'SJ', color: 'bg-blue-600' },
    { name: 'James Wilson', email: 'james.wilson@company.com', jobTitle: 'Finance Analyst', role: 'admin', status: 'active', lastLogin: '21 May 2026', isSelf: false, initials: 'JW', color: 'bg-purple-600' },
    { name: 'Emily Davis', email: 'emily.davis@company.com', jobTitle: 'Document Specialist', role: 'user', status: 'active', lastLogin: '20 May 2026', isSelf: false, initials: 'ED', color: 'bg-green-600' },
    { name: 'Michael Brown', email: 'michael.brown@company.com', jobTitle: 'IT Support', role: 'user', status: 'active', lastLogin: '19 May 2026', isSelf: false, initials: 'MB', color: 'bg-amber-600' },
    { name: 'Lisa Thompson', email: 'lisa.thompson@company.com', jobTitle: '', role: 'user', status: 'inactive', lastLogin: 'Never', isSelf: false, initials: 'LT', color: 'bg-gray-500' },
  ]

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <div className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-400">
            Search users...
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="px-3 py-1.5 text-[10px] font-medium rounded-lg bg-navy-800 text-white">Active</div>
          <div className="px-3 py-1.5 text-[10px] font-medium rounded-lg text-gray-600 dark:text-gray-400">Inactive</div>
          <div className="px-3 py-1.5 text-[10px] font-medium rounded-lg text-gray-600 dark:text-gray-400">All</div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-4 py-2.5 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
              <th className="px-4 py-2.5 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
              <th className="px-4 py-2.5 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Job Title</th>
              <th className="px-4 py-2.5 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
              <th className="px-4 py-2.5 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-2.5 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Login</th>
              <th className="px-4 py-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {users.map((user, i) => (
              <tr key={i} className={`${!user.status === 'active' ? '' : ''} ${user.status === 'inactive' ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-full ${user.color} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-[10px] font-bold text-white">{user.initials}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-medium text-gray-900 dark:text-gray-100 truncate">
                        {user.name}
                        {user.isSelf && <span className="ml-1.5 text-[9px] text-gray-400 dark:text-gray-500 font-normal uppercase tracking-wide">you</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 truncate">{user.email}</td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 truncate">{user.jobTitle || <span className="text-gray-300 dark:text-gray-600">-</span>}</td>
                <td className="px-4 py-3">
                  {user.role === 'admin' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                      <ShieldCheck className="w-3 h-3" /> Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      <UsersIcon className="w-3 h-3" /> User
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {user.status === 'active' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> Inactive
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                  {user.lastLogin === 'Never' ? <span className="text-gray-300 dark:text-gray-600">Never</span> : user.lastLogin}
                </td>
                <td className="px-4 py-3">
                  {!user.isSelf && user.status === 'active' && (
                    <MoreVertical className="w-4 h-4 text-gray-400" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Role Change Modal
// ---------------------------------------------------------------------------
function RoleModalMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 max-w-md shadow-lg">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Change Role</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Update role for <strong className="text-gray-900 dark:text-gray-100">Emily Davis</strong>
      </p>

      <div className="grid grid-cols-2 gap-3 mt-4">
        {/* Admin option - selected */}
        <div className="relative p-3.5 rounded-lg border-2 border-navy-800 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-left">
          <ShieldCheck className="w-5 h-5 mb-1.5 text-navy-800 dark:text-blue-400" />
          <div className="font-medium text-[11px] text-gray-900 dark:text-gray-100">Admin</div>
          <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5">Manage users, templates, and settings</div>
          <div className="absolute top-2 right-2"><Check className="w-3.5 h-3.5 text-navy-800 dark:text-blue-400" /></div>
        </div>
        {/* User option - current */}
        <div className="relative p-3.5 rounded-lg border-2 border-gray-200 dark:border-gray-700 text-left opacity-40">
          <UsersIcon className="w-5 h-5 mb-1.5 text-gray-400" />
          <div className="font-medium text-[11px] text-gray-900 dark:text-gray-100">User</div>
          <div className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5">Process documents using assigned templates</div>
          <span className="absolute top-2 right-2 text-[8px] font-medium text-gray-400 uppercase">Current</span>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <div className="h-7 px-3 border border-gray-300 dark:border-gray-700 rounded-lg flex items-center">
          <span className="text-[10px] text-gray-700 dark:text-gray-200">Cancel</span>
        </div>
        <div className="h-7 px-3 bg-navy-800 rounded-lg flex items-center">
          <span className="text-[10px] text-white font-medium">Confirm Change</span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Actions Dropdown
// ---------------------------------------------------------------------------
function ActionsDropdownMockup() {
  return (
    <div className="inline-flex flex-col items-end">
      <div className="p-1.5 text-gray-400 hover:text-gray-600 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <MoreVertical className="w-4 h-4" />
      </div>
      <div className="mt-1 w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
        <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-gray-700 dark:text-gray-300">
          <UserCog className="w-3.5 h-3.5" /> Change Role
        </div>
        <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
        <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-red-600 dark:text-red-400">
          <X className="w-3.5 h-3.5" /> Deactivate
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mockup: Deactivation Confirmation
// ---------------------------------------------------------------------------
function DeactivateConfirmMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 max-w-md shadow-lg">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Deactivate User</h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">Are you sure you want to deactivate Emily Davis? They will lose access to the application and all templates.</p>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <div className="h-7 px-3 border border-gray-300 dark:border-gray-700 rounded-lg flex items-center">
          <span className="text-[10px] text-gray-700 dark:text-gray-200">Cancel</span>
        </div>
        <div className="h-7 px-3 bg-red-600 rounded-lg flex items-center">
          <span className="text-[10px] text-white font-medium">Deactivate</span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Users Guide Content
// ---------------------------------------------------------------------------
export default function UsersGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <UsersIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Users & Access Management</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage team members, roles, and user permissions.</p>
        </div>
      </div>

      {/* Section 1: Overview & Stats */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">1</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Overview Stats</h3>
        </div>
        <StatsCardsMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>The Users page (admin-only) displays three summary cards at the top:</p>
          <p>• <strong>Active Users</strong> total number of users who currently have access to the application.</p>
          <p>• <strong>Administrators</strong> number of users with the Admin role.</p>
          <p>• <strong>Inactive</strong> number of deactivated users who no longer have access.</p>
        </div>
      </section>

      {/* Section 2: Users Table */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">2</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Users Table</h3>
        </div>
        <UsersTableMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>The main table lists all organisation members with the following columns:</p>


          <h4 className="font-bold text-base text-gray-900 dark:text-gray-100 mt-5 mb-1">Columns</h4>
          <ul className="list-disc pl-8 space-y-1">
            <li><strong>User</strong> avatar (initials with color) and display name. Shows a "YOU" badge next to your own row.</li>
            <li><strong>Email</strong> the user's Microsoft Entra ID email address.</li>
            <li><strong>Job Title</strong> pulled from Microsoft Entra ID profile (may be empty).</li>
            <li><strong>Role</strong> badge showing the person&apos;s role (Admin, Template Manager, Approver, Reviewer, User). People are grouped by role.</li>
            <li><strong>Status</strong> green "Active" or gray "Inactive". Inactive rows are dimmed.</li>
            <li><strong>Last Login</strong> date of the user's most recent sign-in, or "Never" if they haven't logged in.</li>
            <li><strong>Actions</strong> three-dot menu (see Section 3). Not shown for your own row or inactive users.</li>
          </ul>

          <h4 className="font-bold text-base text-gray-900 dark:text-gray-100 mt-5 mb-1">Toolbar</h4>
          <ul className="list-disc pl-8 space-y-1">
            <li><strong>Search</strong> filter users by name or email in real time.</li>
            <li><strong>Status Filter</strong> toggle between "Active", "Inactive", or "All" to quickly find users by their current status.</li>
          </ul>
        </div>
      </section>

      {/* Section 3: User Actions */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">3</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">User Actions</h3>
        </div>
        <div className="flex flex-wrap gap-6 items-start">
          <ActionsDropdownMockup />
        </div>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>Click the three-dot menu (<strong>...</strong>) on any user row to access actions:</p>
          <p>• <strong>Change Role</strong> opens the Role Change modal to promote or demote the user (see Section 4).</p>
          <p>• <strong>Deactivate</strong> removes the user's access to the application (see Section 5).</p>
          <p className="mt-2">Actions are not available for:</p>
          <p>• Your own account (you cannot change your own role or deactivate yourself).</p>
          <p>• Already deactivated users (inactive rows have no action menu).</p>
        </div>
      </section>

      {/* Section 4: Changing Roles */}
      <section className="space-y-4" data-section-number="4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">4</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Changing User Roles</h3>
        </div>
        <RoleModalMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>The Change Role modal lists the roles as selectable cards (the mock-up above is simplified):</p>
          <p>&bull; <strong>Admin</strong> manage users, templates and settings.</p>
          <p>&bull; <strong>Template Manager</strong> manage templates and assign approvers.</p>
          <p>&bull; <strong>Approver</strong> review, approve and publish requests.</p>
          <p>&bull; <strong>Reviewer</strong> send requests for review only.</p>
          <p>&bull; <strong>User</strong> process documents using assigned templates.</p>
          <p>&bull; <strong>Reader</strong> view-only access (only where your organisation has switched it on).</p>
          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Entra ID Sync</p>
          <p>&bull; Role changes are synced to Microsoft Entra ID app roles. If the sync fails the role is still updated in the app and you see a warning.</p>
          <p>&bull; The current role is labelled &quot;Current&quot; and cannot be re-selected. Confirm with <strong>Confirm Change</strong>.</p>
          <p>&bull; When OpCos are switched on, an Admin belongs to one OpCo, and only a Super Admin can put an Admin into a different OpCo.</p>
        </div>
      </section>


      {/* Section 5: Deactivating Users */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">5</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Deactivating Users</h3>
        </div>
        <DeactivateConfirmMockup />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>Deactivating a user removes their access to the application. A confirmation dialog is shown before proceeding.</p>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">What Happens When a User is Deactivated</p>
          <p>• They can no longer sign in to the web app or the Outlook plugin.</p>
          <p>• Their existing extraction requests and data remain in the system (not deleted).</p>
          <p>• They appear in the users table with "Inactive" status and their row is dimmed.</p>
          <p>• Their template access permissions are preserved (in case they are reactivated later).</p>

          <p className="font-semibold text-gray-900 dark:text-gray-100 mt-3">Important Notes</p>
          <p>• Deactivation is reversible - an admin can reactivate the user later.</p>
          <p>• You cannot deactivate yourself.</p>
        </div>
      </section>

      {/* Section 6: Roles and permissions */}
      <section className="space-y-4" data-section-number="6">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">6</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Roles and Permissions</h3>
        </div>
        <Table
          head={['Feature', 'Admin', 'Template Manager', 'Approver', 'Reviewer', 'User']}
          rows={[
            ['Dashboard, Requests, Email Queue, Documents', 'Yes', 'Yes', 'Yes', 'Yes', 'Yes'],
            ['Review extracted data', 'Yes', 'Yes', 'Yes', 'Yes', 'Yes'],
            ['Approve and publish requests', 'Yes', 'Yes', 'Yes', 'No', 'No'],
            ['Approvals page', 'Yes', 'Yes', 'Yes', 'No', 'Yes'],
            ['Create and edit templates, assign approvers', 'Yes', 'Yes', 'No', 'No', 'No'],
            ['Connectors, Output Destinations, Billing, Activity, Settings, Users & Groups', 'Yes', 'No', 'No', 'No', 'No'],
          ]}
        />
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>Template-level access is separate. You only see templates an admin has granted you (Templates &gt; Manage Access).</p>
        </div>
      </section>

      {/* Section 7: Inviting people and groups */}
      <section className="space-y-4" data-section-number="7">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">7</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Inviting People and Groups</h3>
        </div>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>&bull; <strong>Invite Users</strong> opens the invite modal. Pick the role, then <strong>Save &amp; Send Invite</strong>. The invitation email shows the role.</p>
          <p>&bull; If the email could not be delivered, the row offers <strong>Retry Email</strong>. Pending invites offer <strong>Resend Invite</strong>.</p>
          <p>&bull; You can select several people and deactivate them together. Deactivated people move to a <strong>Deactivated Members</strong> section and can be reactivated.</p>
          <p>&bull; Deactivating someone also releases the approvals that were open on them.</p>
          <p>&bull; The <strong>Groups</strong> tab has <strong>Create Group</strong> and an Entra group import. Entra-synced membership is view-only. In a custom group, choose <strong>Save Members</strong> after editing.</p>
        </div>
      </section>

      {/* Section 8: Pages you cannot see */}
      <section className="space-y-4" data-section-number="8">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">8</span>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">If You Are Not an Admin</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Users &amp; Groups is not shown in your sidebar, and opening its address takes you back to Requests.
          Ask an Admin if you need a different role.
        </p>
      </section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">Keep at Least Two Admins</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">Always have at least two admin accounts. If your only admin is deactivated or loses access, you'll be locked out of user management.</p>
          </div>
          <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Info className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-900 dark:text-green-200">Use Restricted Mode for Sensitive Data</span>
            </div>
            <p className="text-[11px] text-green-700 dark:text-green-300 leading-relaxed">If your organisation processes confidential documents, ensure only explicitly approved users can access the system.</p>
          </div>
          <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <ShieldCheck className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-900 dark:text-purple-200">Entra ID Integration</span>
            </div>
            <p className="text-[11px] text-purple-700 dark:text-purple-300 leading-relaxed">User profiles (name, email, job title, avatar) are synced from Microsoft Entra ID. Role changes are also pushed back to Entra ID app roles for consistent RBAC.</p>
          </div>
        </div>
      </section>

      {/* Related */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'templates', label: 'Templates', icon: FileStack },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
